import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { resetPassword } from '../api/auth';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';

/**
 * T125-T128: Password Reset Page
 * 
 * Public page for users to set a new password after clicking reset link in email.
 * Extracts reset token from URL query params and validates it.
 * Includes password strength indicator and comprehensive error handling.
 */
const PasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract token from URL query params
  const queryParams = new URLSearchParams(location.search);
  const resetToken = queryParams.get('token');

  // Form state
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  // Token validation
  const [tokenError, setTokenError] = useState<boolean>(false);
  const [isExpiredToken, setIsExpiredToken] = useState<boolean>(false);

  useEffect(() => {
    // Validate token exists
    if (!resetToken) {
      setTokenError(true);
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [resetToken]);

  /**
   * T126: Password strength calculation
   */
  const calculatePasswordStrength = (password: string): { strength: string; color: string } => {
    if (password.length === 0) {
      return { strength: '', color: '' };
    }

    let score = 0;
    
    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    
    // Complexity checks
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 2) {
      return { strength: 'Weak', color: 'text-error' };
    } else if (score <= 4) {
      return { strength: 'Medium', color: 'text-warning' };
    } else {
      return { strength: 'Strong', color: 'text-success' };
    }
  };

  const passwordStrength = calculatePasswordStrength(newPassword);

  /**
   * T126: Client-side validation
   */
  const validateForm = (): boolean => {
    const errors: string[] = [];

    // Password length check
    if (newPassword.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    // Password match check
    if (newPassword !== confirmPassword) {
      errors.push('Passwords do not match');
    }

    // Password complexity recommendation
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      errors.push('Password should contain uppercase, lowercase, and numbers for better security');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  /**
   * T127-T128: Handle password reset submission
   * Validates form, calls API, handles success/error cases
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous messages
    setError(null);
    setSuccess(null);
    setValidationErrors([]);
    setIsExpiredToken(false);

    // Validate token exists
    if (!resetToken) {
      setError('Invalid reset token');
      return;
    }

    // Client-side validation
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Call API
      await resetPassword(resetToken, newPassword);

      // Show success message
      setSuccess('Password reset successful! Redirecting to login...');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', {
          state: { 
            message: 'Password reset successful. Please login with your new password.' 
          }
        });
      }, 2000);

    } catch (err: any) {
      // T128: Comprehensive error handling
      if (err?.response?.status === 400) {
        const errorDetail = err?.response?.data?.detail || '';
        
        // Check for expired token
        if (errorDetail.toLowerCase().includes('expired')) {
          setIsExpiredToken(true);
          setError('Your reset token has expired. Password reset links are valid for 24 hours.');
        } 
        // Check for used token
        else if (errorDetail.toLowerCase().includes('already been used')) {
          setError('This reset token has already been used. Please request a new password reset if needed.');
        } 
        // Generic invalid token
        else if (errorDetail.toLowerCase().includes('invalid')) {
          setError('Invalid reset token. Please request a new password reset.');
        } 
        // Other 400 errors
        else {
          setError(errorDetail || 'Failed to reset password. Please try again.');
        }
      } else {
        // Network or server errors
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * T128: Handle "Request New Reset" button click
   */
  const handleRequestNewReset = () => {
    navigate('/forgot-password');
  };

  // Show token error state
  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-secondary/10 dark:from-primary/5 dark:via-gray-900 dark:to-secondary/5 px-4">
        <Card variant="default" className="w-full max-w-md">
          <Card.Header 
            title="Invalid Reset Link" 
            subtitle="The password reset link is invalid or missing"
          />
          <Card.Body>
            <div className="rounded-lg mb-4 px-4 py-3 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
            
            <Button 
              variant="primary" 
              onClick={handleRequestNewReset}
              className="w-full"
            >
              Request New Reset
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-secondary/10 dark:from-primary/5 dark:via-gray-900 dark:to-secondary/5 px-4">
      <Card variant="default" className="w-full max-w-md">
        <Card.Header 
          title="Reset Your Password" 
          subtitle="Enter your new password below"
        />
        <Card.Body>
          {/* Success Message */}
          {success && (
            <div className="rounded-lg mb-4 px-4 py-3 bg-success/10 dark:bg-success/20 border border-success/30 text-success">
              <p className="font-semibold">Success</p>
              <p>{success}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="rounded-lg mb-4 px-4 py-3 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="rounded-lg mb-4 px-4 py-3 bg-warning/10 dark:bg-warning/20 border border-warning/30 text-warning">
              <p className="font-semibold mb-2">Please fix the following:</p>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((err, index) => (
                  <li key={index} className="text-sm">{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Request New Reset Button for Expired Tokens */}
          {isExpiredToken && (
            <Button 
              variant="outline" 
              onClick={handleRequestNewReset}
              className="w-full mb-4"
            >
              Request New Reset
            </Button>
          )}

          {/* Password Reset Form */}
          {/* noValidate — see RegisterPage. validateForm checks length, match and complexity,
              so nothing is lost, and "Password must be at least 8 characters long" can actually
              reach the screen instead of being pre-empted by a native tooltip. */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* New Password Field */}
            <div>
              <Input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                disabled={loading || !!success}
                rightIcon={showPassword ? EyeOff : Eye}
                onRightIconClick={() => setShowPassword(!showPassword)}
                rightIconLabel={showPassword ? 'Hide password' : 'Show password'}
              />

              {/* Password Strength Indicator */}
              {newPassword.length > 0 && (
                <div className="mt-2">
                  <p className={`text-sm font-medium ${passwordStrength.color}`}>
                    Strength: {passwordStrength.strength}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <Input
              id="confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              disabled={loading || !!success}
              rightIcon={showConfirmPassword ? EyeOff : Eye}
              onRightIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
              rightIconLabel={showConfirmPassword ? 'Hide password' : 'Show password'}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !!success}
              className="w-full"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PasswordResetPage;
